// ─── Vue arborescente « Par poste » de la page Vulnérabilités ───────────────
// Un appareil = une ligne au chargement (données déjà agrégées côté serveur,
// cf. GET /api/platform/vulnerabilities/by-device) ; le détail logiciel puis
// CVE n'est chargé qu'à l'expansion, pour rester léger avec ~450 appareils et
// ~3 700 CVE ouvertes (cf. CLAUDE.md, page Vulnérabilités).
//
// Trois niveaux :
//   1. Appareil   — chargé au montage de la page, une seule requête.
//   2. Logiciel   — chargé une fois par appareil, à la première expansion
//                   (VulnerabilitiesPage.jsx s'occupe du fetch et du cache).
//   3. CVE        — déjà en mémoire depuis le niveau 2, aucune requête de plus.

import { Fragment } from 'react';
import { ChevronDown, ChevronRight, Loader2, TriangleAlert, Check } from 'lucide-react';
import { cveUrl, fmtRelativeFromNow } from '../lib/format.jsx';
import { MachineTypeBadge } from './MachineTypeBadge.jsx';
import { WindowsIcon, TuxIcon } from './PlatformIcons.jsx';
import { ComitBonChip, ComitTaskButton } from '../lib/comit.jsx';

const SEV_BADGE = {
  CRITICAL: 'bg-crit/15 border-crit/30 text-crit',
  HIGH: 'bg-warn/15 border-warn/30 text-warn',
  MEDIUM: 'bg-blue-500/15 border-blue-500/30 text-blue-400',
  LOW: 'bg-slate-500/15 border-slate-500/30 text-muted',
  UNKNOWN: 'bg-slate-500/10 border-slate-500/20 text-muted-2',
};

const SevBadge = ({ sev }) => (
  <span
    className={`px-2 py-0.5 rounded text-[10px] font-bold tracking-wide uppercase border whitespace-nowrap ${
      SEV_BADGE[sev] || SEV_BADGE.UNKNOWN
    }`}
  >
    {sev || 'unknown'}
  </span>
);

const PILL_TONE = {
  critical: 'bg-crit/15 border-crit/30 text-crit',
  high: 'bg-warn/15 border-warn/30 text-warn',
  medium: 'bg-blue-500/15 border-blue-500/30 text-blue-400',
  low: 'bg-slate-500/15 border-slate-500/30 text-muted',
};

// Pastille de compte par sévérité — masquée à zéro : sur ~450 appareils la
// plupart n'ont pas les quatre niveaux, afficher « 0 faible » partout serait
// du bruit (cf. tâche : « hide zero pills »).
const SevPill = ({ tone, count, label }) => {
  if (!count) return null;
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border whitespace-nowrap ${PILL_TONE[tone]}`}>
      {count} {label}
    </span>
  );
};

const fmtDate = (d) => (d ? new Date(d).toLocaleString('fr-FR') : '—');

const MAX_DESC = 140;
const truncate = (s) => {
  const t = (s || '').trim();
  if (!t) return '—';
  return t.length > MAX_DESC ? t.slice(0, MAX_DESC) + '…' : t;
};

/**
 * Descriptions courte/longue d'un bon issu d'un groupe (logiciel, version)
 * scopé à UN appareil (contrairement à la vue « Par logiciel », qui peut
 * couvrir plusieurs appareils). Auto-suffisant : pas d'import croisé vers
 * VulnerabilitiesPage.jsx, qui possède son propre `buildGroupTaskText` pour
 * la vue à plat.
 */
function buildDeviceSoftwareTaskText(computerName, group) {
  const logiciel = [group.softwareName, group.softwareVersion].filter(Boolean).join(' ');
  const courte = `RMM — ${computerName} : mise à jour ${logiciel || 'logiciel'} (${group.cveCount} CVE)`;
  const lignes = [
    'Bon créé depuis la vue « Par poste » des vulnérabilités Fonxtio RMM.',
    '',
    `Appareil     : ${computerName}`,
    `Logiciel     : ${logiciel || '—'}`,
    `CVE (${group.cveCount}) : ${(group.vulns || []).map((v) => v.cveId).join(', ') || '—'}`,
    `Sévérité max : ${group.maxSeverity || '—'}${
      group.maxCvssScore != null ? ` (CVSS ${group.maxCvssScore.toFixed(1)})` : ''
    }`,
  ];
  return { description: courte.slice(0, 100), descriptiontravail: lignes.join('\n').trim() };
}

export function VulnerabilityDeviceTree({
  rows,
  loading,
  headerProps,
  arrow,
  expanded,
  onToggleExpand,
  details,
  expandedSoftware,
  onToggleSoftware,
  canAssignBon,
  selectedSoftware,
  onToggleSelectSoftware,
  canResolve,
  onResolveCve,
  navigate,
}) {
  const th = (key, label, extra = '') => (
    <th
      {...headerProps(key)}
      className={`px-3 py-2.5 text-left text-muted-2 font-medium text-[11px] uppercase border-b border-rule whitespace-nowrap ${extra}`}
    >
      {label}
      {arrow(key)}
    </th>
  );

  return (
    <div className="bg-card border border-rule rounded-2xl overflow-hidden">
      <table className="w-full text-xs border-collapse">
        <thead className="bg-sidebar">
          <tr>
            <th className="px-3 py-2.5 w-[24px] border-b border-rule" />
            {th('computerName', 'Appareil')}
            {th('clientName', 'Client · Site')}
            {th('critical', 'Sévérités')}
            {th('softwareCount', 'Logiciels')}
            {th('maxCvssScore', 'CVSS max')}
            {th('lastDetectedAt', 'Détection')}
            <th className="px-3 py-2.5 text-left text-muted-2 font-medium text-[11px] uppercase border-b border-rule">
              Bon ComIT
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={8} className="px-3 py-10 text-center text-muted-2 text-xs">
                {loading ? 'Chargement…' : 'Aucun appareil ne correspond aux filtres'}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const isOpen = expanded.has(row.agentId);
              const detail = details[row.agentId];
              const PlatIcon = row.platform === 'linux' ? TuxIcon : WindowsIcon;
              return (
                <Fragment key={row.agentId}>
                  <tr
                    onClick={() => onToggleExpand(row.agentId)}
                    className="border-b border-sidebar cursor-pointer hover:bg-card-hi"
                  >
                    <td className="px-3 py-2">
                      <span className="text-muted inline-flex">
                        {isOpen ? <ChevronDown size={14} strokeWidth={2} /> : <ChevronRight size={14} strokeWidth={2} />}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span
                          className={`w-1.5 h-1.5 rounded-full shrink-0 ${row.isOnline ? 'bg-ok' : 'bg-crit'}`}
                          title={row.isOnline ? 'En ligne' : 'Hors ligne'}
                        />
                        <PlatIcon size={12} className="text-muted shrink-0" />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('deviceDetail', { device: row.agentId });
                          }}
                          className="bg-transparent border-none p-0 text-blue-400 cursor-pointer font-mono hover:underline"
                        >
                          {row.computerName}
                        </button>
                        <MachineTypeBadge machineType={row.machineType} />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-muted text-[11px] truncate max-w-[180px]" title={`${row.clientName || ''} · ${row.siteName || ''}`}>
                      {row.clientName || '—'}{row.siteName ? ` · ${row.siteName}` : ''}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1 flex-wrap">
                        <SevPill tone="critical" count={row.critical} label="critique" />
                        <SevPill tone="high" count={row.high} label="élevée" />
                        <SevPill tone="medium" count={row.medium} label="moyenne" />
                        <SevPill tone="low" count={row.low} label="faible" />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-fg-2 font-mono">{row.softwareCount}</td>
                    <td className="px-3 py-2 text-fg-2 font-mono">
                      {row.maxCvssScore != null ? row.maxCvssScore.toFixed(1) : '—'}
                    </td>
                    <td className="px-3 py-2 text-muted text-[11px]" title={fmtDate(row.lastDetectedAt)}>
                      {row.lastDetectedAt ? `il y a ${fmtRelativeFromNow(row.lastDetectedAt)}` : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {(row.comitBonNumeros || []).length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {row.comitBonNumeros.map((n) => (
                            <ComitBonChip key={n} compact numero={n} />
                          ))}
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-sidebar bg-sidebar/40">
                      <td />
                      <td colSpan={7} className="px-3 py-3">
                        {!detail || detail.loading ? (
                          <div className="flex items-center gap-2 text-muted-2 text-[11px] py-2">
                            <Loader2 size={13} className="animate-spin" /> Chargement des logiciels…
                          </div>
                        ) : detail.error ? (
                          <div className="flex items-center gap-2 text-crit text-[11px] py-2">
                            <TriangleAlert size={13} /> Échec du chargement du détail — réessayer en repliant/dépliant l'appareil.
                          </div>
                        ) : detail.softwareGroups.length === 0 ? (
                          <div className="text-muted-2 text-[11px] py-2">Aucun logiciel ne correspond aux filtres pour cet appareil.</div>
                        ) : (
                          <table className="w-full text-[11px] border-collapse">
                            <tbody>
                              {detail.softwareGroups.map((g) => {
                                const ck = `${row.agentId}::${g.key}`;
                                const swOpen = expandedSoftware.has(ck);
                                return (
                                  <Fragment key={ck}>
                                    <tr className="border-b border-rule/60">
                                      <td className="py-1.5 pr-2 w-[20px]">
                                        <button
                                          onClick={() => onToggleSoftware(ck)}
                                          className="bg-transparent border-none p-0 text-muted cursor-pointer inline-flex hover:text-fg-2"
                                        >
                                          {swOpen ? <ChevronDown size={12} strokeWidth={2} /> : <ChevronRight size={12} strokeWidth={2} />}
                                        </button>
                                      </td>
                                      {canAssignBon && (
                                        <td className="py-1.5 pr-2 w-[20px]">
                                          {g.vulnerabilityIds.length > 0 && (
                                            <input
                                              type="checkbox"
                                              checked={selectedSoftware.includes(ck)}
                                              onChange={() => onToggleSelectSoftware(ck)}
                                            />
                                          )}
                                        </td>
                                      )}
                                      <td className="py-1.5 pr-3 text-fg-2 truncate max-w-[200px]" title={g.softwareName}>
                                        {g.softwareName}
                                      </td>
                                      <td className="py-1.5 pr-3 text-muted font-mono">{g.softwareVersion}</td>
                                      <td className="py-1.5 pr-3">
                                        <SevBadge sev={g.maxSeverity} />
                                      </td>
                                      <td className="py-1.5 pr-3 text-fg-2 font-mono">{g.cveCount}</td>
                                      <td className="py-1.5 pr-2 text-right">
                                        {g.comitBonNumero ? (
                                          <ComitBonChip compact numero={g.comitBonNumero} linkedAt={g.comitBonLinkedAt} linkedBy={g.comitBonLinkedBy} />
                                        ) : (
                                          g.vulnerabilityIds.length > 0 && (
                                            <ComitTaskButton
                                              compact
                                              texte={buildDeviceSoftwareTaskText(row.computerName, g)}
                                              comitClientId={g.comitClientId || 0}
                                              appareil={row.computerName}
                                              cible={{ vulnerabilityIds: g.vulnerabilityIds }}
                                            />
                                          )
                                        )}
                                      </td>
                                    </tr>
                                    {swOpen && (
                                      <tr className="border-b border-rule/60">
                                        <td />
                                        {canAssignBon && <td />}
                                        <td colSpan={4} className="py-2 pl-1">
                                          <table className="w-full text-[10.5px] border-collapse">
                                            <thead>
                                              <tr className="text-muted-2 uppercase text-[9.5px]">
                                                <th className="text-left font-medium pb-1 pr-2">Sévérité</th>
                                                <th className="text-left font-medium pb-1 pr-2">CVSS</th>
                                                <th className="text-left font-medium pb-1 pr-2">CVE</th>
                                                <th className="text-left font-medium pb-1 pr-2">Description</th>
                                                <th className="text-left font-medium pb-1 pr-2">Détectée</th>
                                                <th className="text-right font-medium pb-1">Statut</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {g.vulns.map((v) => (
                                                <tr key={v.id} className={v.isResolved ? 'opacity-60' : ''}>
                                                  <td className="py-1 pr-2 align-top">
                                                    <SevBadge sev={v.cvssSeverity} />
                                                  </td>
                                                  <td className="py-1 pr-2 align-top font-mono text-fg-2">
                                                    {v.cvssScore != null ? v.cvssScore.toFixed(1) : '—'}
                                                  </td>
                                                  <td className="py-1 pr-2 align-top font-mono">
                                                    <a
                                                      href={cveUrl(v.cveId)}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      className="text-blue-400 no-underline hover:underline"
                                                    >
                                                      {v.cveId}
                                                    </a>
                                                  </td>
                                                  <td className="py-1 pr-2 align-top text-muted max-w-[320px]" title={v.cveDescription || ''}>
                                                    {truncate(v.cveDescription)}
                                                  </td>
                                                  <td className="py-1 pr-2 align-top text-muted whitespace-nowrap">
                                                    {fmtDate(v.detectedAt)}
                                                  </td>
                                                  <td className="py-1 align-top text-right whitespace-nowrap">
                                                    {v.isResolved ? (
                                                      <span className="text-ok inline-flex items-center gap-1">
                                                        <Check size={10} strokeWidth={2.5} /> Résolue
                                                      </span>
                                                    ) : canResolve ? (
                                                      <button
                                                        onClick={() => onResolveCve(row.agentId, v.id)}
                                                        className="bg-transparent border border-rule-3 rounded-md px-1.5 py-0.5 text-muted text-[10px] cursor-pointer hover:text-fg-2"
                                                      >
                                                        Résoudre
                                                      </button>
                                                    ) : (
                                                      <span className="text-crit">Active</span>
                                                    )}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </td>
                                      </tr>
                                    )}
                                  </Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
